/**
 * @fileoverview Tipos para centros educativos de la Junta de Andalucía
 * almacenados en Cloudflare R2 para el proyecto PTEL.
 *
 * Fuente: Junta de Andalucía — Datos Abiertos (da_centros.csv)
 * Licencia: CC BY 4.0
 * Coordenadas: EPSG:25830 (reproyectadas desde WGS84)
 *
 * @version 1.0.0
 * @date 2026-02-22
 */

/** Subtipos de centro educativo según clasificación Junta */
export type EduCenterSubtipo =
  | 'CEIP'   // Centro Educación Infantil y Primaria
  | 'IES'    // Instituto Educación Secundaria
  | 'CPR'    // Centro Profesional
  | 'CEO'    // Centro Educación Obligatoria
  | 'EI'     // Escuela Infantil
  | 'OTRO';

/** Titularidad del centro */
export type EduCenterTitularidad = 'Público' | 'Privado' | 'Concertado';

/**
 * Feature de centro educativo normalizada para R2.
 * Esquema compatible con el patrón R2Service del proyecto.
 */
export interface EduCenterFeature {
  /** ID único: "edu-{codigoCentro}" */
  id: string;
  /** Nombre del centro (ej: "CEIP San José") */
  nombre: string;
  /** Clasificación: CEIP | IES | CPR | CEO | EI | OTRO */
  subtipo: string;
  /** Código oficial del centro educativo */
  codigoCentro: string;
  /** Público | Privado | Concertado */
  titularidad: string;
  /** Dirección postal del centro */
  direccion: string;
  /** Localidad */
  localidad: string;
  /** Código INE municipio (5 dígitos) */
  codMun: string;
  /** Nombre del municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Tokens pre-computados para uFuzzy (opcional — si no existe, se calcula al vuelo) */
  tokens?: string;
  /** Fuente de datos */
  fuente: 'junta_educacion';
  /** Tipos de enseñanza ofrecidos */
  ensenanzas: string;
  /** Metadatos adicionales */
  metadata: Record<string, unknown>;
}

// =============================================================================
// CONSTANTES DE VALIDACIÓN
// =============================================================================

/** Rango X válido para coordenadas centros educativos Andalucía (EPSG:25830) */
export const EDU_X_RANGE = { min: 100_000, max: 600_000 } as const;

/** Rango Y válido para coordenadas centros educativos Andalucía (EPSG:25830) */
export const EDU_Y_RANGE = { min: 3_980_000, max: 4_290_000 } as const;

/** Precisión estimada en metros (geolocalización oficial Junta, ±15 m) */
export const EDU_PRECISION_M = 15;

// =============================================================================
// OPCIONES DE BÚSQUEDA Y RESULTADO
// =============================================================================

/** Opciones para búsqueda en EduCentersR2Service */
export interface EduCentersSearchOptions {
  /** Código INE del municipio (5 dígitos) */
  codINE: string;
  /** Nombre de la infraestructura a buscar (fuzzy) */
  name?: string;
  /** Filtrar por subtipo de centro */
  subtipo?: EduCenterSubtipo;
  /** Confianza mínima (0-100), default 55 */
  minConfidence?: number;
  /** Máximo de resultados, default 5 */
  maxResults?: number;
}

/** Resultado intermedio de búsqueda EduCenters */
export interface EduCentersResult {
  x: number;
  y: number;
  confidence: number;
  precision: number;
  source: 'edu_centers_r2';
  feature: EduCenterFeature;
  fuzzyScore?: number;
  matchedName: string;
}
